import { useCallback, useEffect, useMemo, useState } from 'react';

import PlatformIcon from '../components/PlatformIcon';
import { shortDateTime } from '../lib/format';
import { collectScan, isSkipped, startScan } from '../lib/scan';
import type { ScanSkipped, ScanStarted } from '../lib/scan';
import { supabase } from '../lib/supabase';
import type { ScanLane, ScanRun, Source } from '../lib/types';

/**
 * The scan panel: one row per lane.
 *
 * ## Why lanes and not communities
 *
 * A scan is a query, and a query belongs to a lane. The Reddit one is a single phrase search
 * across the whole of Reddit: a matching post is wanted wherever it was posted, so the phrase is
 * the lens and the scan *adds* any community it has not seen before - with links and pitching off,
 * for the operator to review - rather than dropping its results. Putting a phrase on `r/IWantOut`'s
 * row would have been the same string on every row, owned by none of them.
 *
 * Instagram and Facebook are the other way round: the watchlist *is* the search - the tags and
 * the group URLs - so those lanes have no phrase to type, and this panel shows what it will send
 * instead of asking.
 *
 * ## The input is shown, never asked for
 *
 * The first version of this screen put the raw Apify input body in a textarea on every watchlist
 * row. That made the operator responsible for field names that are ours to get right, and it
 * still did not tell them what would actually be sent. Here the input is built in the Worker from
 * verified field names and then *displayed* under "what was sent", so it is ours to write and
 * theirs to check - which is the way round that can be trusted.
 *
 * ## Zero is a result
 *
 * A wrong phrase and a quiet week both leave the review queue empty, and nothing else on this
 * screen can tell them apart. So the result count is stored and shown, and `0` is printed as a
 * warning rather than as a success.
 */

interface Props {
  actor: string;
  /**
   * Bumped by `Setup` whenever a watchlist row is written.
   *
   * Every subject on this panel is derived from the watchlist - the chips, the count on Scan all,
   * and whether that button is disabled - so a source switched on in the section below has to land
   * here without a page reload.
   */
  watchlistVersion: number;
}

/**
 * Every lane in `scan_lanes` now has a verified actor input in `LANE_INPUT`
 * (`workers/brain/src/sweep.ts`), so there is no "not ready" set and no badge any more.
 *
 * `laneBlockedReason()` in the Worker is the ONE authority on whether a lane can run - it refuses
 * a lane with no input builder by name. Do not re-add a mirror of it here: the previous copy said
 * `facebook_groups` was unusable long after the Worker had accepted it, so the panel disabled a
 * button that worked.
 */

/**
 * The watchlist rows a lane reads: its platform, switched on, and - for the two Facebook lanes -
 * on the right side of the `login_required` split.
 *
 * `usableSources()` in `workers/brain/src/sweep.ts` is the authority. This mirrors it because the
 * dashboard cannot import Worker code, and a drift is visible rather than silent - the panel would
 * count a lane as ready that the Worker then refuses.
 */
function enabledFor(lane: ScanLane, sources: Source[]): Source[] {
  if (lane.lane === 'facebook_groups') {
    return sources.filter((source) => source.platform === 'facebook' && source.enabled && !source.login_required);
  }
  if (lane.lane === 'facebook_private_groups') {
    return sources.filter((source) => source.platform === 'facebook' && source.enabled && source.login_required);
  }
  const platform = lane.lane;
  return sources.filter((source) => source.platform === platform && source.enabled);
}

/** One thing a lane will search for, ready to display. `title` is set only when it adds something. */
interface Subject {
  label: string;
  title?: string;
}

/**
 * What a lane will search for, read back off the watchlist.
 *
 * Nothing is typed here - the watchlist *is* the search for these lanes - so this is the panel's
 * half of `subjectsFor()` in sweep.ts, which builds the same list into the actor input.
 */
function subjectsOf(lane: ScanLane, sources: Source[]): Subject[] {
  const usable = enabledFor(lane, sources);

  if (lane.lane === 'instagram') {
    return usable
      .map((source) => /^#(.+)$/.exec(source.identifier)?.[1])
      .filter((tag): tag is string => Boolean(tag))
      .map((tag) => ({ label: `#${tag}` }));
  }

  if (lane.lane === 'facebook_groups' || lane.lane === 'facebook_private_groups') {
    // Stored as `fb/group/<slug>`, which is a path this app invented rather than something to
    // read. The slug is what the operator recognises; the URL the actor is sent is the hover.
    return usable
      .map((source) => /^fb\/group\/(.+)$/i.exec(source.identifier)?.[1])
      .filter((slug): slug is string => Boolean(slug))
      .map((slug) => ({ label: slug, title: `https://www.facebook.com/groups/${slug}` }));
  }

  return [];
}

/** The caption over a lane's subjects. Short, because the lane is already named above it. */
const SUBJECT_LABEL: Record<string, string> = {
  reddit: 'Search phrase',
  instagram: 'Hashtags',
  facebook_groups: 'Groups',
  facebook_private_groups: 'Groups (login required)',
};

/**
 * Display order. Not alphabetical, which is what the query returns and what put the one lane that
 * cannot run above the one that carries the work.
 */
const LANE_ORDER: Record<string, number> = {
  reddit: 0,
  instagram: 1,
  facebook_groups: 2,
  facebook_private_groups: 3,
};

function describe(result: { started: ScanStarted[]; skipped: ScanSkipped[]; warnings: string[] }) {
  const parts: string[] = [];

  for (const run of result.started) {
    if (run.itemsRead === 0) {
      parts.push(`${run.label}: 0 results - check the search phrase`);
    } else if (run.itemsRead === null) {
      parts.push(`${run.label}: started, count pending`);
    } else {
      parts.push(`${run.label}: ${run.itemsRead} queued`);
    }
  }
  for (const run of result.skipped) parts.push(`${run.label}: ${run.reason}`);
  parts.push(...result.warnings);

  return {
    // A scan that started nothing is a refusal to report, not a success to celebrate.
    kind: result.started.length === 0 || result.started.every((run) => run.itemsRead === 0)
      ? ('warn' as const)
      : ('ok' as const),
    text: parts.length > 0 ? parts.join(' · ') : 'Nothing to do.',
  };
}

/**
 * How long a run has been going, for the lane badge.
 *
 * A scan waits up to 25 seconds for Apify before it hands back "still running", and a badge that
 * only says "scanning…" for that long is indistinguishable from one that has hung. The seconds
 * are read off the run's own `started_at`, so they are the server's clock rather than a timer the
 * browser started when the button was clicked.
 */
function elapsedSince(iso: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export default function ScanSection({ actor, watchlistVersion }: Props) {
  const [lanes, setLanes] = useState<ScanLane[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [runs, setRuns] = useState<ScanRun[]>([]);
  const [busy, setBusy] = useState<string[]>([]);
  const [message, setMessage] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * A clock for the elapsed label on a running lane.
   *
   * The interval that drives it is started below only while something is actually live, so an
   * idle Setup page does not re-render once a second for nothing.
   */
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    const [laneResult, sourceResult, runResult] = await Promise.all([
      supabase.from('scan_lanes').select('*').order('lane', { ascending: true }),
      supabase.from('sources').select('*'),
      supabase.from('scan_runs').select('*').order('started_at', { ascending: false }).limit(50),
    ]);

    if (laneResult.error) setError(laneResult.error.message);
    else {
      setError(null);
      setLanes((laneResult.data ?? []) as ScanLane[]);
    }
    setSources((sourceResult.data ?? []) as Source[]);
    setRuns((runResult.data ?? []) as ScanRun[]);
  }, []);

  // Two reasons to reload, and the second one is not about scans: the watchlist. Every subject
  // printed here is derived from the enabled rows of the watchlist the lane searches, so a source
  // switched on in the section below has to restate this panel. `watchlistVersion` is how that
  // arrives (nothing here subscribes to `sources` - that table is not in the realtime publication).
  useEffect(() => {
    void load();
  }, [load, watchlistVersion]);

  useEffect(() => {
    // Realtime rather than a poll: a run's status is written by the Worker, and the row is the
    // only place this browser can learn that a run it started has finished - which is what makes
    // Collect appear by itself rather than on a Refresh click.
    //
    // Its own effect on purpose: folding it in with the reload above would resubscribe on every
    // watchlist edit, and a resubscribe is a visible gap in `scan_runs` updates for no reason.
    const channel = supabase
      .channel('scan-lanes')
      .on('postgres_changes', { event: '*', schema: 'nestmatch', table: 'scan_runs' }, () => {
        void load();
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [load]);

  const liveRuns = useMemo(() => {
    const map = new Map<string, ScanRun>();
    for (const run of runs) {
      if (!run.lane) continue;
      if (run.status !== 'starting' && run.status !== 'running') continue;
      if (!map.has(run.lane)) map.set(run.lane, run);
    }
    return map;
  }, [runs]);

  /** True while a request is in flight, or a run this page knows about is still going. */
  const anyLive = busy.length > 0 || liveRuns.size > 0;

  // Ticks only while something is live. `anyLive` is the whole dependency: when the last run
  // finishes the interval is torn down and the page goes still again.
  useEffect(() => {
    if (!anyLive) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [anyLive]);

  const lastRuns = useMemo(() => {
    const map = new Map<string, ScanRun>();
    for (const run of runs) {
      if (!run.lane) continue;
      if (run.status === 'starting' || run.status === 'running') continue;
      if (!map.has(run.lane)) map.set(run.lane, run);
    }
    return map;
  }, [runs]);

  /** Lanes that would actually be started. A mirror for the button's state; the Worker decides. */
  const ready = lanes.filter((lane) => {
    if (!lane.enabled) return false;
    // A lane that is already running is not "ready": the unique partial index in 0008 refuses a
    // second live run for it, so counting it would offer a button whose only outcome is an error.
    // This is what keeps "Scan all (N)" honest when a session returns to this page mid-scan.
    if (liveRuns.has(lane.lane)) return false;
    // Reddit searches all of Reddit now, so it needs no watchlist row to be ready - the Worker's
    // `laneBlockedReason()` is the authority, and it asks this lane only for a phrase. The other
    // two lanes *are* the watchlist: Instagram is handed the hashtags, Facebook the group URLs.
    if (lane.lane !== 'reddit' && enabledFor(lane, sources).length === 0) return false;
    if (lane.lane === 'reddit') return Boolean((lane.term ?? '').trim());
    return subjectsOf(lane, sources).length > 0;
  });

  /** Lanes a click could not start because a run for them is already going. */
  const running = lanes.filter((lane) => lane.enabled && liveRuns.has(lane.lane));

  /** The order the lanes are drawn in, which is not the order the query returns them in. */
  const ordered = [...lanes].sort(
    (a, b) => (LANE_ORDER[a.lane] ?? 9) - (LANE_ORDER[b.lane] ?? 9),
  );

  async function scan(which?: string[]) {
    const key = which?.[0] ?? 'all';
    setBusy((current) => [...current, key]);
    setMessage(null);
    const result = await startScan(which);
    setBusy((current) => current.filter((candidate) => candidate !== key));

    if (!result.ok || !result.data) {
      setMessage({ kind: 'warn', text: result.error ?? 'The scan could not be started.' });
      return;
    }
    setMessage(describe(result.data));
    await load();
  }

  async function collect(run: ScanRun) {
    const key = run.lane ?? run.id;
    setBusy((current) => [...current, key]);
    setMessage(null);
    const result = await collectScan(run.id);
    setBusy((current) => current.filter((candidate) => candidate !== key));

    if (!result.ok || !result.data) {
      setMessage({ kind: 'warn', text: result.error ?? 'Could not collect that scan.' });
      return;
    }

    const data = result.data;
    setMessage(
      isSkipped(data)
        ? { kind: 'warn', text: `${data.label}: ${data.reason}` }
        : {
            kind: data.itemsRead === 0 ? 'warn' : 'ok',
            text:
              data.itemsRead === 0
                ? `${data.label}: 0 results - check the search phrase`
                : `${data.label}: ${data.itemsRead ?? 'some'} queued.`,
          },
    );
    await load();
  }

  /** Only the fields this screen owns. Everything else about a lane is code, not config. */
  async function save(lane: ScanLane, patch: Partial<ScanLane>, said: string) {
    const { error: writeError } = await supabase
      .from('scan_lanes')
      .update({ ...patch, updated_by: actor })
      .eq('lane', lane.lane);
    if (writeError) setError(writeError.message);
    else setMessage({ kind: 'ok', text: said });
    await load();
  }

  return (
    <section className="setup-section">
      <h3>Scan</h3>
      <p className="muted small">
        Starts one Apify run per lane and queues what it finds. Spends the shared credit, posts
        nothing.
      </p>

      {error ? <p className="error">{error}</p> : null}
      {message ? (
        <p className={message.kind === 'ok' ? 'notice' : 'error'} role="status">
          {message.text}
        </p>
      ) : null}

      <div className="scan-bar">
        <button
          type="button"
          disabled={ready.length === 0 || busy.includes('all')}
          title={
            ready.length === 0
                ? running.length > 0
                  ? 'Every ready lane is already scanning.'
                  : 'No lane ready — each needs to be on, with something to search for.'
                : `Start ${ready.length} run${ready.length === 1 ? '' : 's'} — spends credit, posts nothing.`
          }
          onClick={() => void scan()}
        >
          {busy.includes('all') ? 'Scanning…' : `Scan all (${ready.length})`}
        </button>

        {/*
          The answer to "can I leave this page?", and it is not decoration: the run happens in
          the Worker and in Apify, so closing the tab does not stop it. Shown only while something
          is actually live, so it does not become permanent prose on the page.
        */}
        {anyLive ? (
          <span className="muted small">Runs on the server — you can leave this page.</span>
        ) : null}
      </div>

      {lanes.length === 0 && !error ? (
        <p className="muted">No lanes yet. Apply migration 0008.</p>
      ) : null}

      {ordered.map((lane) => {
        const live = liveRuns.get(lane.lane);
        const last = lastRuns.get(lane.lane);
        const subjects = subjectsOf(lane, sources);
        const isBusy = busy.includes(lane.lane);

        return (
          // A lane switched off will not run, so it is drawn inert.
          <div className={lane.enabled ? 'lane' : 'lane off'} key={lane.lane}>
            <div className="lane-head">
              <PlatformIcon platform={lane.platform} size={16} />
              <strong>{lane.label}</strong>
              <span className="grow" />

              {/* The lane's own action lives in the head, so it is in the same place on every row. */}
              <span className="lane-actions">
                {live ? (
                  <>
                    <span className="badge warn">
                      {live.status === 'starting' ? 'starting the actor' : 'scanning'}…{' '}
                      {elapsedSince(live.started_at, now)}
                    </span>
                    <button
                      type="button"
                      className="link"
                      disabled={isBusy}
                      onClick={() => void collect(live)}
                    >
                      Collect results
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="secondary"
                    disabled={isBusy || !lane.enabled}
                    onClick={() => void scan([lane.lane])}
                  >
                    {isBusy ? 'Scanning…' : 'Scan'}
                  </button>
                )}
              </span>

              <label className="toggle" title="Switch this lane off to stop spending on it">
                <input
                  type="checkbox"
                  checked={lane.enabled}
                  aria-label={`${lane.label} switched on`}
                  onChange={() =>
                    void save(
                      lane,
                      { enabled: !lane.enabled },
                      lane.enabled ? `${lane.label} switched off.` : `${lane.label} switched on.`,
                    )
                  }
                />
              </label>
            </div>

            <div className="lane-body">
              <div>
                {lane.lane === 'reddit' ? (
                  <>
                    <label htmlFor={`term-${lane.lane}`}>{SUBJECT_LABEL[lane.lane]}</label>
                    <input
                      id={`term-${lane.lane}`}
                      type="text"
                      defaultValue={lane.term ?? ''}
                      placeholder="moving to the uk"
                      onBlur={(event) => {
                        const next = event.target.value.trim() || null;
                        if (next !== lane.term) {
                          void save(lane, { term: next }, 'Search phrase saved.');
                        }
                      }}
                    />
                    <div className="muted small lane-hint">
                      One phrase, searched across all of Reddit. New communities are added with
                      links off.
                    </div>
                  </>
                ) : (
                  <>
                    <span className="lane-caption">{SUBJECT_LABEL[lane.lane]}</span>
                    {subjects.length === 0 ? (
                      <p className="muted small" style={{ margin: 0 }}>
                        None enabled in the watchlist.
                      </p>
                    ) : (
                      <div className="badges">
                        {subjects.map((subject) => (
                          <span className="badge" key={subject.label} title={subject.title}>
                            {subject.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label htmlFor={`items-${lane.lane}`}>Results per run</label>
                <input
                  id={`items-${lane.lane}`}
                  type="number"
                  min={1}
                  max={50}
                  className="lane-num"
                  defaultValue={lane.max_items}
                  onBlur={(event) => {
                    const next = Number(event.target.value);
                    if (!Number.isFinite(next) || next === lane.max_items) return;
                    void save(
                      lane,
                      { max_items: Math.min(Math.max(Math.round(next), 1), 50) },
                      'Saved.',
                    );
                  }}
                />
              </div>
            </div>

            {last ? (
              <div className="lane-foot">
                <span className={last.items_read === 0 ? 'small warn' : 'muted small'}>
                  {last.status === 'failed'
                    ? `last scan failed${last.error ? `: ${last.error.slice(0, 80)}` : ''}`
                    : last.items_read === 0
                      ? 'last scan: 0 results'
                      : last.items_read === null
                        ? `scanned ${shortDateTime(last.started_at)}`
                        : `${last.items_read} results, ${shortDateTime(last.started_at)}`}
                </span>
                {last.input && Object.keys(last.input).length > 0 ? (
                  <details className="scan-sent">
                    <summary>what was sent</summary>
                    <pre>{JSON.stringify(last.input, null, 2)}</pre>
                  </details>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
